import React, { useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { alpha } from "@mui/material/styles";
import {
  Box,
  Typography,
  Alert,
  Button,
  TextField,
  Stack,
  Tooltip,
  CircularProgress,
  Paper,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { getBpePaymentStatus } from "../../api/bpeApi";

/**
 * Exibe Pix copia-e-cola ou link de pagamento e faz polling do status.
 */
const BPePaymentPanel = ({
  conversationId,
  chargeData,
  paymentMethodId,
  onPaid,
  onSendToChat,
  polling = true,
  autoEmitOnPaid = true,
}) => {
  const { t } = useTranslation("chat");
  const [checking, setChecking] = React.useState(false);
  const paidHandledRef = React.useRef(false);

  React.useEffect(() => {
    paidHandledRef.current = false;
  }, [chargeData?.transactionId, chargeData?.checkoutUrl, chargeData?.copyPaste]);

  const pollStatus = useCallback(async () => {
    if (!conversationId || !chargeData || paidHandledRef.current) return;
    setChecking(true);
    try {
      const params = {
        id_formadepagamento: paymentMethodId,
        provider: chargeData.provider,
        transactionId: chargeData.transactionId,
      };
      const st = await getBpePaymentStatus(conversationId, params);
      if (st?.status === "paid") {
        paidHandledRef.current = true;
        if (onPaid) onPaid(st);
      }
    } catch {
      /* ignore poll errors */
    } finally {
      setChecking(false);
    }
  }, [conversationId, chargeData, paymentMethodId, onPaid]);

  useEffect(() => {
    if (!polling || !chargeData) return undefined;
    pollStatus();
    const id = setInterval(pollStatus, 4000);
    return () => clearInterval(id);
  }, [polling, chargeData, pollStatus]);

  if (!chargeData) return null;

  const handleCopyPix = async () => {
    if (!chargeData.copyPaste) return;
    try {
      await navigator.clipboard.writeText(chargeData.copyPaste);
    } catch {
      /* ignore */
    }
  };

  const handleSendPixToChat = () => {
    if (!chargeData.copyPaste || !onSendToChat) return;
    onSendToChat(
      `${t("bpeWizard.pixMessageIntro")}\n\n${chargeData.copyPaste}\n\n${t("bpeWizard.pixMessageFooter")}`
    );
  };

  const handleSendLinkToChat = () => {
    if (!chargeData.checkoutUrl || !onSendToChat) return;
    onSendToChat(
      `${t("bpeWizard.linkMessageIntro")}\n${chargeData.checkoutUrl}\n\n${t("bpeWizard.linkMessageFooter")}`
    );
  };

  const waitingStatus =
    autoEmitOnPaid && polling ? (
      <Paper
        variant="outlined"
        sx={(theme) => ({
          p: 1.5,
          mb: 2,
          borderRadius: 2,
          borderColor: alpha(theme.palette.primary.main, 0.35),
          bgcolor: alpha(theme.palette.primary.main, 0.05),
        })}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={(theme) => ({
              width: 38,
              height: 38,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
            })}
          >
            <CircularProgress size={20} thickness={5} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} color="primary.main" lineHeight={1.2}>
              {t("bpeWizard.autoPaymentTitle")}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
              {t("bpeWizard.autoPaymentDesc")}
            </Typography>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    bgcolor: "success.main",
                    flexShrink: 0,
                    animation: "bpePulse 1.4s ease-in-out infinite",
                    "@keyframes bpePulse": {
                      "0%, 100%": { opacity: 1 },
                      "50%": { opacity: 0.25 },
                    },
                  }}
                />
                <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap>
                  {checking ? t("bpeWizard.autoPaymentChecking") : t("bpeWizard.autoPaymentMonitoring")}
                </Typography>
              </Stack>
              <Button
                size="small"
                variant="text"
                onClick={pollStatus}
                disabled={checking}
                sx={{ textTransform: "none", minWidth: 0, py: 0, px: 0.5, fontSize: "0.72rem", flexShrink: 0 }}
              >
                {t("bpeWizard.checkPayment")}
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Paper>
    ) : null;

  if (chargeData.kind === "pix") {
    return (
      <Box>
        {waitingStatus}
        {!autoEmitOnPaid && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("bpeWizard.pixInstructions")}
          </Alert>
        )}
        <TextField
          label={t("bpeWizard.pixCopyPaste")}
          value={chargeData.copyPaste || ""}
          multiline
          minRows={3}
          fullWidth
          InputProps={{ readOnly: true }}
        />
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
          <Tooltip title={t("bpeWizard.assist.help.payment")} arrow>
            <Button startIcon={<VisibilityOutlinedIcon />} onClick={handleSendPixToChat} variant="contained" sx={{ flex: 1, minWidth: 160 }}>
              {t("bpeWizard.sendPixToChat")}
            </Button>
          </Tooltip>
          <Button startIcon={<ContentCopyIcon />} onClick={handleCopyPix} variant="outlined">
            {t("bpeWizard.copyPix")}
          </Button>
        </Stack>
        {chargeData.expiresAt && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {t("bpeWizard.pixExpires", {
              time: new Date(chargeData.expiresAt).toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit",
              }),
            })}
          </Typography>
        )}
        {!autoEmitOnPaid && (
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={pollStatus} disabled={checking} fullWidth>
              {checking ? <CircularProgress size={20} /> : t("bpeWizard.checkPayment")}
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  if (chargeData.kind === "link" && chargeData.checkoutUrl) {
    return (
      <Box>
        {waitingStatus}
        {!autoEmitOnPaid && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("bpeWizard.linkInstructions")}
          </Alert>
        )}
        <Stack spacing={1}>
          <Tooltip title={t("bpeWizard.assist.help.payment")} arrow>
            <Button
              variant="contained"
              size="large"
              startIcon={<VisibilityOutlinedIcon />}
              onClick={handleSendLinkToChat}
              fullWidth
              sx={{ fontWeight: 700 }}
            >
              {t("bpeWizard.sendLinkToChat")}
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            href={chargeData.checkoutUrl}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewIcon />}
            fullWidth
          >
            {t("bpeWizard.openPaymentLink")}
          </Button>
          {!autoEmitOnPaid && (
            <Button variant="outlined" onClick={pollStatus} disabled={checking} fullWidth>
              {checking ? <CircularProgress size={20} /> : t("bpeWizard.checkPayment")}
            </Button>
          )}
        </Stack>
      </Box>
    );
  }

  if (chargeData.kind === "card") {
    const status = String(chargeData.status || "").toUpperCase();
    if (status === "PAID" && chargeData.bpeAuto) {
      return <Alert severity="success">{t("bpeWizard.cardPaid")}</Alert>;
    }
    if (status === "PENDING") {
      return (
        <Alert severity="warning">
          {t("bpeWizard.cardPending")}
          <Button size="small" onClick={pollStatus} sx={{ mt: 1 }} disabled={checking}>
            {t("bpeWizard.checkPayment")}
          </Button>
        </Alert>
      );
    }
    return <Alert severity="error">{t("bpeWizard.cardRefused")}</Alert>;
  }

  return null;
};

export default BPePaymentPanel;
